// @flow
import { ErrorMessage } from 'formik';
import * as React from 'react';
import { Box, Text, CheckBox as GrommetCheckbox } from 'grommet';
import { withTheme } from 'styled-components';
import * as Yup from 'yup';
import { defineMessages } from 'react-intl';
import FormStylesContext from 'speed-js-react-web/src/context/FormStylesContext';
import RequiredAsterisk from '../RequiredAsterisk';


type Props = {
    field: {
        onChange: (e: SyntheticInputEvent<number>) => void,
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        value: any;
        name: string;
    },
    form: { isSubmitting: boolean },
    required: boolean,
    label: string,
    help_text: string,
    theme: Object,
    disabled?: boolean,
}


const Checkbox = (props: Props): React.Node => {
    const {
        disabled,
        field: {
            name, onChange, onBlur, value,
        },
        form: { isSubmitting },
        label,
        help_text: helpText,
        required,
        theme,
    } = props;

    const { formField: { error: errorStyle, label: labelStyle, help: helpStyle } } = theme;

    const [styling, setStyling] = React.useState('');

    const styles = React.useContext(FormStylesContext);

    const handleChange = React.useCallback((e) => {
        onBlur(e);
        onChange(e);
    }, [onBlur, onChange]);

    const checkBoxTheme = {
        border: { color: { dark: styling, light: styling }, width: '2px' },
        check: { radius: '4px', thickness: '4px', extend: { borderColor: styling, borderWidth: '3px' } },
        hover: { border: { color: { dark: 'white', light: 'black' } } },
        icon: {},
        icons: {},
        size: '26px',
        toggle: {
            color: { dark: '#d9d9d9', light: '#d9d9d9' }, knob: {}, radius: '26px', size: '52px',
        },
    };

    theme.checkBox = checkBoxTheme;

    React.useEffect(() => {
        const stylingBox = styles[name]?.apply ? 'rgba(253,208,137,1)' : 'rgba(0,0,0,0.15)';
        setStyling(stylingBox);
    }, [styles[name]]);

    return (
        <Box flex={false} margin={{ vertical: 'small' }}>
            <GrommetCheckbox
                name={name}
                checked={value || false}
                onChange={handleChange}
                onBlur={onBlur}
                label={(
                    <Text>
                        {label}
                        {required ? <RequiredAsterisk /> : null}
                    </Text>
                )}
                disabled={disabled || isSubmitting}
            />
            {helpText && (
                <Text {...helpStyle}>{helpText}</Text>
            )}
            <ErrorMessage name={name}>
                {(msg) => <Text {...errorStyle}>{msg}</Text>}
            </ErrorMessage>
        </Box>
    );
};

const messages = defineMessages({
    required: {
        id: 'checkbox-required',
        defaultMessage: 'This field is required.',
    },
});

Checkbox.getValidationSchema = (field: { validation: Object }) => {
    if (field.validation.required) {
        return Yup.string().required(messages.required).nullable();
    }

    return Yup.string().nullable();
};

Checkbox.getEmptyInitialValues = () => false;
Checkbox.defaultProps = {
    disabled: false,
};

export default withTheme(Checkbox);
